import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $createParagraphNode,
  COMMAND_PRIORITY_HIGH,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_DOWN_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
} from '@lexical/list';
import {
  $createHeadingNode,
  $createQuoteNode,
} from '@lexical/rich-text';
// import { $createCodeNode } from '@lexical/code';
import { $setBlocksType } from '@lexical/selection';
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
} from '@lexical/utils';

export default function ShortcutsPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        const { key, ctrlKey, metaKey, shiftKey, altKey } = event;
        const mod = ctrlKey || metaKey;

        // --- Block format shortcuts ---
        // Ctrl+Alt+0 = Normal
        if (mod && altKey && key === '0') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createParagraphNode());
            }
          });
          return true;
        }
        // Ctrl+Alt+1 = Heading 1
        if (mod && altKey && key === '1') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode('h1'));
            }
          });
          return true;
        }
        // Ctrl+Alt+2 = Heading 2
        if (mod && altKey && key === '2') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode('h2'));
            }
          });
          return true;
        }
        // Ctrl+Alt+3 = Heading 3
        if (mod && altKey && key === '3') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode('h3'));
            }
          });
          return true;
        }
        // Ctrl+Alt+4 = Heading 4
        if (mod && altKey && key === '4') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode('h4'));
            }
          });
          return true;
        }
        // Ctrl+Shift+7 = Numbered List (Shift+7 = '&')
        if (mod && shiftKey && (key === '&' || key === '7')) {
          event.preventDefault();
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          return true;
        }
        // Ctrl+Shift+8 = Bullet List (Shift+8 = '*')
        if (mod && shiftKey && (key === '*' || key === '8')) {
          event.preventDefault();
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          return true;
        }
        // Ctrl+Shift+9 = Check List (Shift+9 = '(')
        if (mod && shiftKey && (key === '(' || key === '9')) {
          event.preventDefault();
          editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
          return true;
        }
        // Ctrl+Shift+Q = Quote
        if (mod && shiftKey && (key === 'q' || key === 'Q')) {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createQuoteNode());
            }
          });
          return true;
        }
        // Ctrl+Alt+C = Code Block (disabled)
        // if (mod && altKey && (key === 'c' || key === 'C')) {
        //   event.preventDefault();
        //   editor.update(() => {
        //     const selection = $getSelection();
        //     if ($isRangeSelection(selection)) {
        //       if (selection.isCollapsed()) {
        //         $setBlocksType(selection, () => $createCodeNode());
        //       } else {
        //         const textContent = selection.getTextContent();
        //         const codeNode = $createCodeNode();
        //         selection.insertNodes([codeNode]);
        //         const newSelection = $getSelection();
        //         if ($isRangeSelection(newSelection)) {
        //           newSelection.insertRawText(textContent);
        //         }
        //       }
        //     }
        //   });
        //   return true;
        // }

        // --- Text transform shortcuts ---
        // Ctrl+Shift+1 = Lowercase
        if (mod && shiftKey && key === '!') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.getNodes().forEach((node) => {
                if ($isTextNode(node)) {
                  node.setTextContent(node.getTextContent().toLowerCase());
                }
              });
            }
          });
          return true;
        }
        // Ctrl+Shift+2 = Uppercase
        if (mod && shiftKey && key === '@') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.getNodes().forEach((node) => {
                if ($isTextNode(node)) {
                  node.setTextContent(node.getTextContent().toUpperCase());
                }
              });
            }
          });
          return true;
        }
        // Ctrl+Shift+3 = Capitalize
        if (mod && shiftKey && key === '#') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.getNodes().forEach((node) => {
                if ($isTextNode(node)) {
                  node.setTextContent(
                    node.getTextContent().replace(/\b\w/g, (c) => c.toUpperCase())
                  );
                }
              });
            }
          });
          return true;
        }
        // Ctrl+Shift+X = Strikethrough
        if (mod && shiftKey && (key === 'x' || key === 'X')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          return true;
        }
        // Ctrl+, = Subscript
        if (mod && !shiftKey && !altKey && key === ',') {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
          return true;
        }
        // Ctrl+. = Superscript
        if (mod && !shiftKey && !altKey && key === '.') {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
          return true;
        }
        // Ctrl+\ = Clear Formatting
        if (mod && !shiftKey && !altKey && key === '\\') {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              const nodes = selection.getNodes();
              const extractedNodes = selection.extract();
              nodes.forEach((node, idx) => {
                if ($isTextNode(node)) {
                  let textNode = idx === 0 ? extractedNodes[0] : node;
                  if (textNode.__style !== '') {
                    textNode = textNode.setStyle('');
                  }
                  if (textNode.__format !== 0) {
                    textNode = textNode.setFormat(0);
                    $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
                  }
                }
              });
            }
          });
          return true;
        }

        // --- Alignment shortcuts ---
        // Ctrl+Shift+L = Left Align
        if (mod && shiftKey && (key === 'l' || key === 'L')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
          return true;
        }
        // Ctrl+Shift+E = Center Align
        if (mod && shiftKey && (key === 'e' || key === 'E')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
          return true;
        }
        // Ctrl+Shift+R = Right Align
        if (mod && shiftKey && (key === 'r' || key === 'R')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
          return true;
        }
        // Ctrl+Shift+J = Justify Align
        if (mod && shiftKey && (key === 'j' || key === 'J')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
          return true;
        }

        // --- Indent shortcuts ---
        // Ctrl+[ = Outdent
        if (mod && !shiftKey && !altKey && key === '[') {
          event.preventDefault();
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          return true;
        }
        // Ctrl+] = Indent
        if (mod && !shiftKey && !altKey && key === ']') {
          event.preventDefault();
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}
